import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { images } from '../data/mockData';
import { getResponsiveImageUrl } from '../lib/imageOptimization';
import {
  fetchPractitionerById,
  fetchPractitioners,
  fetchReviewsForPractitioner,
  fetchVideoReviewsForPractitioner,
  submitReviewForPractitioner,
} from '../lib/supabase';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '../components/ui/carousel';
import { formatPractitionerName, formatProfileText } from '../lib/profileFormatting';
import { updateSocialMetadata, upsertMeta } from '../lib/seoMetadata';
import VideoReviewPlayer from '../components/VideoReviewPlayer';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

const VideoReviewCarousel = ({ videoReviews }) => (
  <div className="mb-12">
    <div className="flex items-center justify-between gap-4 mb-5">
      <div>
        <h3 className="text-2xl font-bold text-purple-800 font-title">Video Reviews</h3>
        <p className="text-purple-600 font-body mt-1">
          Hear directly from clients about their experience.
        </p>
      </div>
    </div>

    <Carousel
      opts={{ align: 'start', loop: videoReviews.length > 1 }}
      className="w-full px-1 sm:px-10"
    >
      <CarouselContent className="-ml-4">
        {videoReviews.map((review, index) => (
          <CarouselItem
            key={review.id || index}
            className="pl-4 basis-full md:basis-1/2 lg:basis-1/3"
          >
            <Card className="h-full overflow-hidden bg-white border border-purple-200">
              <div className="aspect-video bg-purple-950">
                <VideoReviewPlayer review={review} />
              </div>
              <CardContent className="p-5">
                <h4 className="font-semibold text-purple-800 font-subtitle">
                  {review.reviewer_name || 'Anonymous Client'}
                </h4>
                {review.caption && (
                  <p className="mt-2 text-purple-700 leading-relaxed font-body">
                    {review.caption}
                  </p>
                )}
              </CardContent>
            </Card>
          </CarouselItem>
        ))}
      </CarouselContent>
      {videoReviews.length > 1 && (
        <>
          <CarouselPrevious
            className="left-0 sm:left-1 bg-white text-purple-700 border-purple-200 hover:bg-purple-50"
            aria-label="Previous video review"
          />
          <CarouselNext
            className="right-0 sm:right-1 bg-white text-purple-700 border-purple-200 hover:bg-purple-50"
            aria-label="Next video review"
          />
        </>
      )}
    </Carousel>
  </div>
);

const renderBioParagraphs = (bio) =>
  String(bio)
    .split(/\n\s*\n/)
    .map(formatProfileText)
    .filter(Boolean)
    .map((paragraph, index) => (
      <p key={index} className="mb-6">{paragraph}</p>
    ));

const PractitionerDetailPage = () => {
  const { practitionerId } = useParams();
  const [practitioner, setPractitioner] = useState(null);
  const [allPractitioners, setAllPractitioners] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [videoReviews, setVideoReviews] = useState([]);
  const [offerPages, setOfferPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedReviews, setExpandedReviews] = useState({});
  const [reviewForm, setReviewForm] = useState({
    reviewer_name: '',
    rating: 5,
    comment: '',
    website: '',
  });
  const [reviewSubmitting, setReviewSubmitting] = useState(false);
  const [reviewSubmissionMessage, setReviewSubmissionMessage] = useState({ type: '', text: '' });
  
  useEffect(() => {
    const loadPractitioner = async () => {
      setLoading(true);
      try {
        // Fetch the specific practitioner
        const practitionerData = await fetchPractitionerById(practitionerId);
        setPractitioner(practitionerData);
        
        // Fetch all practitioners for navigation
        const allPractitionersData = await fetchPractitioners();
        setAllPractitioners(allPractitionersData);

        // Fetch text and video reviews for this practitioner
        const [reviewsData, videoReviewsData] = await Promise.all([
          fetchReviewsForPractitioner(practitionerId),
          fetchVideoReviewsForPractitioner(practitionerId),
        ]);
        setReviews(reviewsData);
        setVideoReviews(videoReviewsData);
      } catch (error) {
        console.error('Error loading practitioner:', error);
      }
      setLoading(false);
    };

    loadPractitioner();
  }, [practitionerId]);

  useEffect(() => {
    let active = true;
    fetch(`${API_BASE}/api/practitioners/${encodeURIComponent(practitionerId)}/landing-pages`)
      .then(async (response) => {
        if (response.status === 404) return [];
        if (!response.ok) throw new Error('Could not load practitioner offers.');
        return response.json();
      })
      .then((items) => {
        if (active) setOfferPages(Array.isArray(items) ? items : []);
      })
      .catch((error) => console.error(error));
    return () => {
      active = false;
    };
  }, [practitionerId]);

  useEffect(() => {
    if (!practitioner) return;

    const practitionerName = formatPractitionerName(practitioner.Name);
    const descriptionSource =
      practitioner['Short Bio'] ||
      practitioner['Detailed Bio'] ||
      practitioner.Bio ||
      `${practitionerName} offers verified spiritual and holistic services through The Wisdom Network.`;
    const description = String(descriptionSource).replace(/\s+/g, ' ').trim().slice(0, 300);
    const title = `${practitionerName} | The Wisdom Network`;
    const hostname = window.location.hostname.toLowerCase();
    const siteOrigin =
      hostname === 'thewisdomnetwork.net' || hostname.endsWith('.thewisdomnetwork.net')
        ? 'https://www.thewisdomnetwork.net'
        : window.location.origin;
    const canonicalUrl = `${siteOrigin}/practitioners/${practitioner.slug || practitionerId}`;

    document.title = title;
    upsertMeta('meta[name="description"]', { name: 'description' }, description);
    updateSocialMetadata({ title, description, url: canonicalUrl, type: 'profile' });

    let canonical = document.head.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', canonicalUrl);

    const existingStructuredData = document.head.querySelector('script[data-practitioner-structured-data]');
    existingStructuredData?.remove();
    const structuredData = document.createElement('script');
    structuredData.type = 'application/ld+json';
    structuredData.dataset.practitionerStructuredData = 'true';
    structuredData.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'Person',
      name: practitionerName,
      jobTitle: formatProfileText(practitioner.Title) || undefined,
      description,
      image: practitioner.Photo || undefined,
      url: canonicalUrl,
      worksFor: {
        '@type': 'Organization',
        name: 'The Wisdom Network',
        url: `${siteOrigin}/`,
      },
    });
    document.head.appendChild(structuredData);

    return () => {
      structuredData.remove();
    };
  }, [practitioner, practitionerId]);
  
  // Get current practitioner index for navigation
  const currentIndex = allPractitioners.findIndex(p => p.ID.toString() === practitionerId);
  const previousPractitioner = currentIndex > 0 ? allPractitioners[currentIndex - 1] : null;
  const nextPractitioner = currentIndex < allPractitioners.length - 1 ? allPractitioners[currentIndex + 1] : null;

  if (loading) {
    return (
      <div className="min-h-screen bg-white pt-20 flex items-center justify-center">
        <div className="text-center">
          <div className="text-xl text-purple-800 font-body">Loading practitioner...</div>
        </div>
      </div>
    );
  }

  if (!practitioner) {
    return (
      <div className="min-h-screen bg-white pt-20 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-purple-800 mb-4">Practitioner Not Found</h1>
          <p className="text-purple-600 mb-8">The practitioner you're looking for doesn't exist.</p>
          <Button 
            onClick={() => window.history.back()}
            className="bg-purple-600 hover:bg-purple-700 text-white"
          >
            Go Back
          </Button>
        </div>
      </div>
    );
  }

  const handleBookConsultation = () => {
    const consultationLink = practitioner.Consultation || 'https://thewisdomnetwork.as.me/';
    window.open(consultationLink, '_blank', 'noopener noreferrer');
  };

  const handleBookSession = () => {
    const bookingLink = practitioner['Book Me'] || 'https://thewisdomnetwork.as.me/';
    window.open(bookingLink, '_blank', 'noopener noreferrer');
  };

  const handleOngoingSupport = () => {
    const packagesLink = practitioner.packages;
    if (packagesLink) {
      window.open(packagesLink, '_blank', 'noopener noreferrer');
    } else {
      handleBookSession(); // Fallback to regular booking
    }
  };

  // Helper function to get first 2 sentences
  const getFirstTwoSentences = (text) => {
    if (!text) return '';
    
    // Clean the text
    const cleanText = text.trim();
    
    // Split by sentence endings - more aggressive approach
    // Look for periods, exclamation marks, or question marks
    let sentences = [];
    let currentSentence = '';
    
    for (let i = 0; i < cleanText.length; i++) {
      const char = cleanText[i];
      currentSentence += char;
      
      // Check if we hit a sentence ending
      if (['.', '!', '?'].includes(char)) {
        // Make sure it's not an abbreviation (simple check)
        const nextChar = cleanText[i + 1];
        if (!nextChar || nextChar === ' ' || nextChar === '\n') {
          sentences.push(currentSentence.trim());
          currentSentence = '';
          
          // Stop if we have 2 sentences
          if (sentences.length === 2) {
            break;
          }
        }
      }
    }
    
    // If we didn't get 2 complete sentences, add the remaining text as the last sentence
    if (sentences.length < 2 && currentSentence.trim()) {
      sentences.push(currentSentence.trim());
    }
    
    // If we still have fewer than 2 sentences, return the original text
    if (sentences.length < 2) {
      return cleanText;
    }
    
    return sentences.slice(0, 2).join(' ').trim();
  };

  // Toggle expanded state for a review
  const toggleReviewExpansion = (reviewIndex) => {
    setExpandedReviews(prev => ({
      ...prev,
      [reviewIndex]: !prev[reviewIndex]
    }));
  };

  const handleReviewSubmit = async (event) => {
    event.preventDefault();
    setReviewSubmitting(true);
    setReviewSubmissionMessage({ type: '', text: '' });
    try {
      await submitReviewForPractitioner(practitionerId, reviewForm);
      setReviewForm({ reviewer_name: '', rating: 5, comment: '', website: '' });
      setReviewSubmissionMessage({
        type: 'success',
        text: 'Thank you. Your review was submitted and is waiting for admin approval.',
      });
    } catch (error) {
      setReviewSubmissionMessage({
        type: 'error',
        text: error.message || 'Could not submit your review. Please try again.',
      });
    } finally {
      setReviewSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white pt-20">
      {/* Hero Section */}
      <section className="py-16 bg-gradient-to-br from-purple-200 to-purple-100 min-h-screen flex items-center">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left Column - Large Oval Photo */}
            <div className="flex justify-center lg:justify-end">
              <div className="relative">
                <img
                  src={getResponsiveImageUrl(
                    practitioner.Photo || 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHwxfHxwb3J0cmFpdCUyMHdvbWFufGVufDB8fHx8MTc1NTg5NTgwOXww&ixlib=rb-4.1.0&q=85',
                    { width: 1000, quality: 80 },
                  )}
                  alt={practitioner.Name}
                  className="w-96 h-[500px] rounded-[200px] shadow-2xl object-cover border-4 border-white"
                  fetchPriority="high"
                  decoding="async"
                />
              </div>
            </div>

            {/* Right Column - Name, Title & Booking Buttons */}
            <div className="space-y-8 text-center lg:text-left">
              <div>
                <h1 className="text-5xl lg:text-6xl font-bold text-purple-800 mb-6 font-title leading-tight">
                  {formatPractitionerName(practitioner.Name)}
                </h1>
                <h2 className="text-2xl lg:text-3xl font-semibold text-yellow-600 mb-8 font-subtitle">
                  {formatProfileText(practitioner.Title)}
                </h2>
              </div>

              {/* Booking Buttons Stack */}
              <div className="space-y-4 max-w-md mx-auto lg:mx-0">
                <Button 
                  onClick={handleBookConsultation}
                  className="w-full bg-purple-600 hover:bg-purple-700 text-white font-bold py-4 px-8 text-lg rounded-xl transition-all duration-300 transform hover:scale-105 shadow-lg font-body"
                >
                  Book a Consultation
                </Button>
                
                <Button 
                  onClick={handleBookSession}
                  className="w-full bg-purple-600 hover:bg-purple-700 text-white font-bold py-4 px-8 text-lg rounded-xl transition-all duration-300 transform hover:scale-105 shadow-lg font-body"
                >
                  Book a Single Session
                </Button>
                
                {practitioner.packages && (
                  <Button 
                    onClick={handleOngoingSupport}
                    className="w-full bg-gradient-to-r from-yellow-500 to-yellow-400 hover:from-yellow-400 hover:to-yellow-300 text-black font-bold py-4 px-8 text-lg rounded-xl transition-all duration-300 transform hover:scale-105 shadow-lg font-body"
                  >
                    Ongoing Spiritual Support
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {offerPages.length > 0 && (
        <section className="bg-[#f5f0f8] px-4 py-14 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-6xl">
            <p className="text-xs font-semibold uppercase tracking-[0.22em] text-purple-600">Ways to work together</p>
            <h2 className="mt-2 font-title text-3xl font-bold text-purple-950">Featured offers</h2>
            <div className="mt-6 grid gap-4 sm:grid-cols-2">
              {offerPages.map((offer) => (
                <Link
                  key={offer.slug}
                  to={`/offers/${encodeURIComponent(offer.slug)}`}
                  className="group flex items-center justify-between gap-4 rounded-xl border border-purple-100 bg-white p-5 shadow-sm transition hover:-translate-y-0.5 hover:border-purple-300 hover:shadow-md"
                >
                  <span>
                    <span className="block font-semibold text-purple-950">{offer.title}</span>
                    <span className="mt-1 block text-sm text-purple-700">{offer.cta_text || 'Learn more'}</span>
                  </span>
                  <ArrowRight className="h-5 w-5 shrink-0 text-purple-600 transition-transform group-hover:translate-x-1" />
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Services and Qualifications Grid */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Services Offered */}
              <div className="bg-purple-50 p-6 rounded-lg">
                <h3 className="text-2xl font-bold text-purple-800 mb-4 font-subtitle">Services Offered</h3>
                <p className="text-purple-700 leading-relaxed font-body">
                  {formatProfileText(
                    practitioner.practices || 'Spiritual guidance and holistic healing services',
                  )}
                </p>
              </div>

              {/* Qualifications */}
              {practitioner.Certifications && (
                <div className="bg-purple-50 p-6 rounded-lg">
                  <h3 className="text-2xl font-bold text-purple-800 mb-4 font-subtitle">Qualifications</h3>
                  <p className="text-purple-700 leading-relaxed font-body">
                    {formatProfileText(practitioner.Certifications)}
                  </p>
                </div>
              )}

              {/* Specializes In */}
              {formatProfileText(practitioner['Specializes In']) && (
                <div className="bg-purple-50 p-6 rounded-lg">
                  <h3 className="text-2xl font-bold text-purple-800 mb-4 font-subtitle">Specializes In</h3>
                  <p className="text-purple-700 leading-relaxed font-body">
                    {formatProfileText(practitioner['Specializes In'])}
                  </p>
                </div>
              )}

              {/* Gender Focus */}
              {formatProfileText(practitioner['Gender Focus']) && (
                <div className="bg-purple-50 p-6 rounded-lg">
                  <h3 className="text-2xl font-bold text-purple-800 mb-4 font-subtitle">Gender Focus</h3>
                  <p className="text-purple-700 leading-relaxed font-body">
                    {formatProfileText(practitioner['Gender Focus'])}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="text-center">
              <h3 className="text-3xl font-bold text-purple-800 mb-6 font-title">About</h3>
              <div className="text-purple-700 leading-relaxed font-body text-lg">
                {(() => {
                  const bio = practitioner['Detailed Bio'] || practitioner.Bio || practitioner['Short Bio'];
                  if (!bio) {
                    return renderBioParagraphs(
                      'Dedicated spiritual practitioner committed to helping clients achieve personal growth and transformation.',
                    );
                  }
                  
                  // Try to parse JSON if it looks like structured content
                  if (typeof bio === 'string' && bio.includes('nodes')) {
                    try {
                      const parsed = JSON.parse(bio);
                      if (parsed.nodes && Array.isArray(parsed.nodes)) {
                        return parsed.nodes.map((node, index) => {
                          if (node.type === 'PARAGRAPH' && node.nodes) {
                            const text = formatProfileText(
                              node.nodes.map((textNode) => textNode.textData?.text || '').join(''),
                            );
                            return text ? <p key={index} className="mb-6">{text}</p> : null;
                          }
                          return null;
                        }).filter(Boolean);
                      }
                    } catch (e) {
                      // If parsing fails, return as plain text
                      return renderBioParagraphs(bio);
                    }
                  }
                  
                  return renderBioParagraphs(bio);
                })()}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Client Reviews Section */}
      <section className="py-16 bg-purple-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-purple-800 mb-4 font-title">Client Reviews</h2>
            <p className="text-purple-600 font-body">
              Results may vary for each individual. The experiences shared by our clients reflect their personal journey and should not be interpreted as guaranteed outcomes.
            </p>
          </div>

          {/* Video Reviews Carousel */}
          {videoReviews.length > 0 && <VideoReviewCarousel videoReviews={videoReviews} />}

          {reviews && reviews.length > 0 && (
            <div className="mb-5">
              <h3 className="text-2xl font-bold text-purple-800 font-title">Written Reviews</h3>
            </div>
          )}

          {/* Reviews from Database */}
          {reviews && reviews.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
              {reviews.map((review, index) => {
                const fullText = review.Comment || review.comment || review.review || review.Review || 'Great experience working with this practitioner.';
                const truncatedText = getFirstTwoSentences(fullText);
                const isExpanded = expandedReviews[index];
                const needsExpansion = fullText.length > truncatedText.length;
                const rating = Math.min(5, Math.max(1, Number(review.Rating ?? review.rating) || 5));

                return (
                  <Card 
                    key={review.id || index}
                    className="bg-white border border-purple-200 hover:border-purple-400 transition-all duration-300"
                  >
                    <CardContent className="p-6">
                      {/* Header with Name and Stars */}
                      <div className="mb-4">
                        <h4 className="font-semibold text-purple-800 font-subtitle mb-2">
                          {review['Reviewer Name'] || review.reviewer_name || 'Anonymous Client'}
                        </h4>
                        <div
                          className="flex items-center mb-3"
                          role="img"
                          aria-label={`Rated ${rating} out of 5 stars`}
                        >
                          {[...Array(5)].map((_, starIndex) => (
                            <svg
                              key={starIndex}
                              className={`w-5 h-5 fill-current ${starIndex < rating ? 'text-yellow-400' : 'text-gray-300'}`}
                              viewBox="0 0 20 20"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                fillRule="evenodd"
                                d="M10 15.27L16.18 19l-1.64-7.03L20 7.24l-7.19-.61L10 0 7.19 6.63 0 7.24l5.46 4.73L3.82 19z"
                                clipRule="evenodd"
                              />
                            </svg>
                          ))}
                        </div>
                      </div>
                      
                      {/* Review Text - Truncated or Full */}
                      <div className="text-purple-700 leading-relaxed font-body">
                        <p className="mb-3">
                          {isExpanded ? fullText : truncatedText}
                        </p>
                        
                        {/* Read More/Less Button */}
                        {needsExpansion && (
                          <button
                            onClick={() => toggleReviewExpansion(index)}
                            className="text-purple-600 hover:text-purple-800 font-semibold text-sm underline transition-colors duration-200"
                          >
                            {isExpanded ? 'Read Less' : 'Read More'}
                          </button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          ) : (
            // Fallback to client reviews field if no database reviews
            practitioner['client reviews'] && (
              <div className="mb-12">
                <Card className="bg-white border border-purple-200">
                  <CardContent className="p-8">
                    <div className="prose prose-purple max-w-none">
                      <p className="text-purple-700 leading-relaxed font-body whitespace-pre-line">
                        {practitioner['client reviews']}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )
          )}

          <Card className="mx-auto max-w-3xl border border-purple-200 bg-white">
            <CardContent className="p-6 sm:p-8">
              <h3 className="font-title text-2xl font-bold text-purple-800">Share your experience</h3>
              <p className="mt-2 text-sm leading-6 text-purple-700">
                Reviews are added to this profile after admin approval. Your name will be shown with your review, or you can leave it blank to submit as Anonymous Client.
              </p>

              <form onSubmit={handleReviewSubmit} className="mt-6 space-y-5">
                <label className="block">
                  <span className="text-sm font-medium text-gray-800">Your name <span className="font-normal text-gray-500">(optional)</span></span>
                  <input
                    className="mt-1 w-full rounded-md border border-purple-200 bg-white px-3 py-2 text-sm text-gray-900 focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-200"
                    value={reviewForm.reviewer_name}
                    maxLength={120}
                    autoComplete="name"
                    onChange={(event) => setReviewForm((current) => ({ ...current, reviewer_name: event.target.value }))}
                  />
                </label>

                <label className="block">
                  <span className="text-sm font-medium text-gray-800">Your rating</span>
                  <select
                    className="mt-1 w-full rounded-md border border-purple-200 bg-white px-3 py-2 text-sm text-gray-900 focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-200"
                    value={reviewForm.rating}
                    required
                    onChange={(event) => setReviewForm((current) => ({ ...current, rating: Number(event.target.value) }))}
                  >
                    {[5, 4, 3, 2, 1].map((ratingValue) => (
                      <option key={ratingValue} value={ratingValue}>
                        {ratingValue} {ratingValue === 1 ? 'star' : 'stars'}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="block">
                  <span className="text-sm font-medium text-gray-800">Your review</span>
                  <textarea
                    className="mt-1 w-full rounded-md border border-purple-200 bg-white px-3 py-2 text-sm leading-6 text-gray-900 focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-200"
                    rows={5}
                    minLength={10}
                    maxLength={4000}
                    required
                    value={reviewForm.comment}
                    onChange={(event) => setReviewForm((current) => ({ ...current, comment: event.target.value }))}
                  />
                  <span className="mt-1 block text-right text-xs text-gray-500">
                    {reviewForm.comment.length}/4000
                  </span>
                </label>

                <label
                  aria-hidden="true"
                  className="absolute -left-[10000px] top-auto h-px w-px overflow-hidden"
                >
                  Leave this field empty
                  <input
                    name="website"
                    tabIndex={-1}
                    autoComplete="off"
                    value={reviewForm.website}
                    onChange={(event) => setReviewForm((current) => ({ ...current, website: event.target.value }))}
                  />
                </label>

                <Button type="submit" disabled={reviewSubmitting} className="bg-purple-700 hover:bg-purple-800">
                  {reviewSubmitting ? 'Submitting...' : 'Submit review'}
                </Button>
                {reviewSubmissionMessage.text && (
                  <p
                    role="status"
                    aria-live="polite"
                    className={`text-sm ${reviewSubmissionMessage.type === 'success' ? 'text-green-700' : 'text-red-600'}`}
                  >
                    {reviewSubmissionMessage.text}
                  </p>
                )}
              </form>
            </CardContent>
          </Card>

        </div>
      </section>

      {/* Navigation Footer */}
      <section className="py-8 bg-purple-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center">
            <div>
              {previousPractitioner && (
                <Button 
                  onClick={() => window.location.href = `/practitioners/${previousPractitioner.ID}`}
                  className="flex items-center bg-purple-600 hover:bg-purple-700 text-white font-body"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Previous Practitioner
                </Button>
              )}
            </div>
            
            <div>
              {nextPractitioner && (
                <Button 
                  onClick={() => window.location.href = `/practitioners/${nextPractitioner.ID}`}
                  className="flex items-center bg-purple-600 hover:bg-purple-700 text-white font-body"
                >
                  Next Practitioner
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PractitionerDetailPage;